import { Link } from '@tanstack/react-router'
import { ArrowRight, Truck } from 'lucide-react'
import {
  type ApInvoiceShipmentAllocation,
  type ApInvoiceVariance,
  type WwexCarrierInvoice,
} from '@/features/invoices/view-models'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { formatMoney, formatStatus } from '@/lib/utils'

function parseTrackingNumbers(value: string | null | undefined) {
  if (!value) return []
  try {
    const parsed: unknown = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : []
  } catch {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
}

interface ChargeParty {
  name?: string | null
}

interface SuggestedPurchaseOrder {
  uuid?: string | null
  po_number?: string | null
  project_uuid?: string | null
  project_name?: string | null
}

// What the matcher recorded about a charge: who shipped it to whom, and how it was matched.
function chargeEvidence(evidence: Record<string, unknown> | null | undefined) {
  const shipper = evidence?.shipper as ChargeParty | null | undefined
  const receiver = evidence?.receiver as ChargeParty | null | undefined
  const method = evidence?.match_method
  const wwexRate = evidence?.wwex_rate as { amount?: string | null } | null | undefined
  return {
    // WWEX's price for the boxes on a charge with no booked cost.
    wwexPrice: wwexRate?.amount != null ? Number(wwexRate.amount) : null,
    shipper: shipper?.name ?? null,
    receiver: receiver?.name ?? null,
    method: method === 'pickup_fee' || method === 'shipper_receiver_date' ? method : null,
    suggested: (evidence?.suggested_purchase_order ?? null) as SuggestedPurchaseOrder | null,
  }
}

export function FreightChargeReview({
  carrierInvoices,
  allocations,
  variances,
}: {
  carrierInvoices: WwexCarrierInvoice[]
  allocations: ApInvoiceShipmentAllocation[]
  variances: ApInvoiceVariance[]
}) {
  const allocationById = new Map(allocations.map((allocation) => [allocation.id, allocation]))
  const varianceByAllocationId = new Map<number, ApInvoiceVariance>()
  const activeFreightVariances = [...variances]
    .filter(
      (variance) =>
        variance.category === 'freight' &&
        variance.shipment_allocation_id != null &&
        !['superseded', 'void'].includes(variance.status),
    )
    .sort((left, right) => left.id - right.id)
  activeFreightVariances.forEach((variance) =>
    varianceByAllocationId.set(variance.shipment_allocation_id!, variance),
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Truck className="h-4 w-4" /> Shipment charges
        </CardTitle>
        <CardDescription>
          Charges match by PRO, BOL or tracking number, or by shipper, customer and ship date.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {carrierInvoices.map((carrierInvoice) => {
          const linkedAllocationIds = new Set(
            carrierInvoice.charge_rows.flatMap((row) =>
              row.allocation_id ? [row.allocation_id] : [],
            ),
          )
          const currentAllocations = allocations.filter(
            (allocation) =>
              linkedAllocationIds.has(allocation.id) &&
              ['proposed', 'confirmed'].includes(allocation.review_status),
          )
          const allocatedTotal = currentAllocations.reduce(
            (total, allocation) => total + Number(allocation.allocation_amount),
            0,
          )
          const selectedQuoteTotal = currentAllocations.reduce(
            (total, allocation) =>
              total + Number(allocation.expected_payable?.selected_quote_amount_snapshot ?? 0),
            0,
          )
          const bookedTotal = currentAllocations.reduce(
            (total, allocation) => total + Number(allocation.expected_payable?.amount ?? 0),
            0,
          )
          const comparedAllocatedTotal = currentAllocations
            .filter((allocation) => allocation.expected_payable)
            .reduce((total, allocation) => total + Number(allocation.allocation_amount), 0)
          const freightVarianceTotal = comparedAllocatedTotal - bookedTotal
          const feeTotal = carrierInvoice.charge_rows
            .filter((row) => row.charge_type !== 'shipment')
            .reduce((total, row) => total + Number(row.amount), 0)
          const needsReview = carrierInvoice.review_status !== 'ready'
          const chargeRows = [...carrierInvoice.charge_rows].sort(
            (left, right) => left.row_number - right.row_number,
          )
          const firstRowByAllocationId = new Map<number, number>()
          chargeRows.forEach((row) => {
            if (row.allocation_id && !firstRowByAllocationId.has(row.allocation_id)) {
              firstRowByAllocationId.set(row.allocation_id, row.id)
            }
          })

          return (
            <section
              key={carrierInvoice.id}
              aria-labelledby={`wwex-invoice-${carrierInvoice.id}`}
              className="overflow-hidden rounded-lg border"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/20 px-3 py-2.5">
                <h3 id={`wwex-invoice-${carrierInvoice.id}`} className="font-semibold">
                  {carrierInvoice.invoice_number || 'WWEX invoice'}
                </h3>
                <Badge variant={needsReview ? 'destructive' : 'secondary'}>
                  {needsReview ? 'Needs review' : 'Ready'}
                </Badge>
              </div>
              <dl className="grid grid-cols-2 border-b text-center sm:grid-cols-3 lg:grid-cols-6 lg:divide-x">
                <div className="px-2 py-2">
                  <dt className="text-xs text-muted-foreground">Invoice</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatMoney(carrierInvoice.invoice_total)}
                  </dd>
                </div>
                <div className="px-2 py-2">
                  <dt className="text-xs text-muted-foreground">Selected quotes</dt>
                  <dd className="font-semibold tabular-nums">{formatMoney(selectedQuoteTotal)}</dd>
                </div>
                <div className="border-t px-2 py-2 sm:border-t-0 lg:border-t-0">
                  <dt className="text-xs text-muted-foreground">Booked</dt>
                  <dd className="font-semibold tabular-nums">{formatMoney(bookedTotal)}</dd>
                </div>
                <div className="border-t px-2 py-2 lg:border-t-0">
                  <dt className="text-xs text-muted-foreground">Carrier allocated</dt>
                  <dd className="font-semibold tabular-nums">{formatMoney(allocatedTotal)}</dd>
                </div>
                <div className="border-t px-2 py-2 lg:border-t-0">
                  <dt className="text-xs text-muted-foreground">Freight variance</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatMoney(freightVarianceTotal)}
                  </dd>
                </div>
                <div className="border-t px-2 py-2 lg:border-t-0">
                  <dt className="text-xs text-muted-foreground">Separate charges</dt>
                  <dd className="font-semibold tabular-nums">{formatMoney(feeTotal)}</dd>
                </div>
              </dl>
              {carrierInvoice.sum_status !== 'matched' && (
                <p className="border-b bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                  Parsed charges do not equal the invoice total.
                </p>
              )}
              <div className="divide-y">
                {chargeRows.map((row) => {
                  const allocation = row.allocation_id
                    ? allocationById.get(row.allocation_id)
                    : undefined
                  const tracking = parseTrackingNumbers(row.tracking_numbers_json)
                  const references = [
                    row.pro_number && `PRO ${row.pro_number}`,
                    row.bol_number && `BOL ${row.bol_number}`,
                    row.booking_reference && `Booking ${row.booking_reference}`,
                    row.wwex_transaction_id && `Transaction ${row.wwex_transaction_id}`,
                    ...tracking.map((number) => `Tracking ${number}`),
                  ].filter(Boolean)
                  const showsAllocation =
                    allocation && firstRowByAllocationId.get(allocation.id) === row.id
                  const variance = allocation
                    ? varianceByAllocationId.get(allocation.id)
                    : undefined
                  const evidence = chargeEvidence(row.match_evidence)
                  const isPickupFee = evidence.method === 'pickup_fee'
                  const suggested = row.match_status === 'matched' ? null : evidence.suggested
                  const settled = allocation?.review_status === 'confirmed'
                  const allocationRows = allocation
                    ? chargeRows.filter((item) => item.allocation_id === allocation.id)
                    : []
                  const wwexPrices = allocationRows.map(
                    (item) => chargeEvidence(item.match_evidence).wwexPrice,
                  )
                  const wwexPrice =
                    allocation &&
                    !allocation.expected_payable &&
                    wwexPrices.some((price) => price != null)
                      ? wwexPrices.reduce<number>((total, price) => total + (price ?? 0), 0)
                      : null

                  return (
                    <article key={row.id} className="space-y-2 px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium">
                            {row.charge_type === 'fee'
                              ? 'Invoice fee'
                              : [row.carrier_name, row.scac_code].filter(Boolean).join(' · ') ||
                                'Shipment charge'}
                          </p>
                          {references.length > 0 && (
                            <p className="mt-0.5 break-words font-mono text-xs text-muted-foreground">
                              {references.join(' · ')}
                            </p>
                          )}
                          {evidence.shipper && (
                            <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                              <span>{evidence.shipper}</span>
                              {evidence.receiver && (
                                <span className="inline-flex items-center gap-1">
                                  <ArrowRight className="h-3 w-3" aria-label="to" />
                                  {evidence.receiver}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        <p className="shrink-0 font-semibold tabular-nums">
                          {formatMoney(row.amount)}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            row.match_status === 'matched' &&
                            (allocation?.expected_payable !== null || settled)
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {row.match_status !== 'matched'
                            ? formatStatus(row.match_status)
                            : isPickupFee
                              ? 'Pickup fee'
                              : !showsAllocation
                                ? 'Included in match'
                                : evidence.method
                                  ? 'Matched by shipper and date'
                                  : allocation.expected_payable
                                    ? 'Auto matched'
                                    : settled && wwexPrice != null
                                      ? 'WWEX price'
                                      : 'No booked cost'}
                        </Badge>
                        {suggested?.uuid && (
                          <>
                            <span className="text-xs text-muted-foreground">
                              No shipment recorded on
                            </span>
                            <Link
                              to="/purchase-orders/$poId"
                              params={{ poId: suggested.uuid }}
                              className="font-medium text-primary hover:underline"
                            >
                              {suggested.po_number || 'Purchase order'}
                            </Link>
                            {suggested.project_uuid && (
                              <Link
                                to="/projects/$projectId"
                                params={{ projectId: suggested.project_uuid }}
                                className="font-medium text-primary hover:underline"
                              >
                                {suggested.project_name || 'Project'}
                              </Link>
                            )}
                          </>
                        )}
                        {showsAllocation && (
                          <>
                            <Link
                              to="/shipments/$shipmentId"
                              params={{
                                shipmentId: allocation.shipment_uuid_snapshot,
                              }}
                              className="font-medium text-primary hover:underline"
                            >
                              Shipment
                            </Link>
                            {allocation.purchase_order_uuid_snapshot && (
                              <Link
                                to="/purchase-orders/$poId"
                                params={{
                                  poId: allocation.purchase_order_uuid_snapshot,
                                }}
                                className="font-medium text-primary hover:underline"
                              >
                                {allocation.po_number_snapshot || 'Purchase order'}
                              </Link>
                            )}
                            {allocation.project_uuid_snapshot && (
                              <Link
                                to="/projects/$projectId"
                                params={{
                                  projectId: allocation.project_uuid_snapshot,
                                }}
                                className="font-medium text-primary hover:underline"
                              >
                                {allocation.project_name_snapshot || 'Project'}
                              </Link>
                            )}
                            <span className="text-xs font-medium tabular-nums text-muted-foreground">
                              Allocated {formatMoney(allocation.allocation_amount)}
                            </span>
                          </>
                        )}
                      </div>
                      {showsAllocation && (
                        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-4">
                          {[
                            [
                              'Selected quote',
                              allocation.expected_payable?.selected_quote_amount_snapshot,
                            ],
                            wwexPrice != null
                              ? ['WWEX price', wwexPrice]
                              : ['Booked', allocation.expected_payable?.amount],
                            ['Carrier invoice', allocation.allocation_amount],
                            ['Variance', variance?.variance_amount],
                          ].map(([label, value]) => (
                            <div key={String(label)} className="bg-background px-2.5 py-2">
                              <dt className="text-xs text-muted-foreground">{label}</dt>
                              <dd className="mt-0.5 font-mono text-sm font-semibold tabular-nums">
                                {value == null ? '—' : formatMoney(value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </article>
                  )
                })}
              </div>
            </section>
          )
        })}
      </CardContent>
    </Card>
  )
}
